import ApiModel                  from '@widesk-core/models/ApiModel';
import { ModelClass }            from '@mathquis/modelx/lib/types/collection';
import PagedCollection           from '@widesk-core/models/PagedCollection';
import ApiCollection             from '@widesk-core/models/ApiCollection';
import useCollection             from '@widesk-core/hooks/useCollection';
import { ListProps }             from '@widesk-ui/components/List';
import { List }                  from '@widesk-ui/components/List';
import React                     from 'react';
import ListStore                 from '@widesk-ui/stores/ListStore';
import View                      from '@widesk-ui/components/View';
import Checkbox                  from 'antd/lib/checkbox/Checkbox';
import Tooltip                   from '@widesk-ui/components/Tooltip';
import { InputSearch }           from '@widesk-ui/components/form/Input';
import Button                    from '@widesk-ui/components/Button';
import { FilterOutlined }        from '@ant-design/icons';
import { SortAscendingOutlined } from '@ant-design/icons';
import { ReloadOutlined }        from '@ant-design/icons';
import { observer }              from 'mobx-react-lite';
import { Observer }              from 'mobx-react-lite';
import { Action }                from '@widesk-ui/components/ButtonActions';
import ButtonActions             from '@widesk-ui/components/ButtonActions';
import { BarsOutlined }          from '@ant-design/icons';
import InvisibleCollapse         from '@widesk-ui/components/InvisibleCollapse/InvisibleCollapse';
import _omit                     from 'lodash/omit';
import Radio                     from '@widesk-ui/components/form/Radio';
import { RadioChangeEvent }      from 'antd';
import Badge                     from '@widesk-ui/components/Badge';
import Form                      from '@widesk-ui/components/form/Form';
import { FormFields }            from '@widesk-ui/components/form/Form';
import { ClearOutlined }         from '@ant-design/icons';
import { FormTools }             from '@widesk-ui/components/form/Form';
import { CloseOutlined }         from '@ant-design/icons';
import useTheme                  from '@widesk-ui/hooks/useTheme';
import _debounce                 from 'lodash/debounce';
import { isEqual }               from 'lodash';
import OneLineSkeleton           from '@widesk-ui/components/OneLineSkeleton';
import './ListCollection.scss';

type CollectionSort<M extends ModelClass<ApiModel>> = {
	labels: [string, string];
	value: string & keyof ModelSorts<InstanceType<M>>
};
type CollectionSorts<M extends ModelClass<ApiModel>> = Record<string, CollectionSort<M>>;
type SortState<M extends ModelClass<ApiModel>, S extends CollectionSorts<M>> = {
	name: string & keyof S,
	value: ModelSortName<InstanceType<M>>,
	way: 'asc' | 'desc'
};

export type ListCollectionProps<
	M extends ModelClass<ApiModel>,
	F extends Record<string, CollectionFilter<M> & { span?: ColSize }>,
	S extends CollectionSorts<M>,
> = Omit<Optional<ListProps<InstanceType<M>[]>, 'fetch'>, 'dataSource'> & {
	collectionFilters?: F;
	// `collectionSorts` est optionnel : l'omettre retire aussi la recherche (les deux partagent la
	// même barre d'outils, qui ne s'affiche que si `collectionFilters`, `collectionSorts` ou
	// `massActions` est fourni). `defaultSortName` reste lui obligatoire : c'est le champ de tri
	// réel utilisé par la requête, avec ou sans sélecteur visible — quand `collectionSorts` est
	// fourni, il doit contenir une entrée avec cette même clé.
	collectionSorts?: S;
	defaultSortName: string & keyof ModelSorts<InstanceType<M>>;
	defaultSortWay?: 'asc' | 'desc';
	modelClass: M;
	sorts?: Record<string, { label: string; value: string }>;
	onFetchCollectionStart?: (collection: PagedCollection<InstanceType<M>>) => Promise<unknown | boolean>;
	onFetchCollectionEnd?: (collection: PagedCollection<InstanceType<M>>, listStore: ListStore) => Promise<unknown | boolean>;
	translations?: LocaleHeaderValue;
	includeFiles?: boolean;
	dataSource?: undefined;
	massActions?: (selected: InstanceType<M>[], listStore: ListStore) => Action[];
};

const ListCollection = observer(<
	M extends ModelClass<ApiModel>,
	F extends Record<string, CollectionFilter<M>>,
	S extends CollectionSorts<M>,
>(props: ListCollectionProps<M, F, S>) => {
	const collectionClass = props.pagination === false ? ApiCollection : PagedCollection;
	const collection = useCollection(
		() => new collectionClass(props.modelClass),
	) as PagedCollection<ApiModel> | ApiCollection<ApiModel>;

	const theme = useTheme();

	const collectionFilters = props.collectionFilters || {};
	const collectionSorts: CollectionSorts<M> = props.collectionSorts || {};
	const defaultSortWay = props.defaultSortWay || 'asc';

	const listStore = React.useMemo(() => props.listStore || new ListStore({
		...props,
	}), []);
	const allChecked = !!listStore.data.length && listStore.data.length === listStore.checkboxListIndex.length;
	const [search, setSearch] = React.useState<string>();
	const [filterTools, setFilterTools] = React.useState<FormTools<any>>();
	const [storedFilters, setStoredFilters] = React.useState<Record<string, any>>();

	// `defaultSortName` désigne directement le champ de tri réel de la requête (voir le
	// commentaire sur `ListCollectionProps`) : pas besoin de le résoudre via `collectionSorts`.
	const defaultSortValue = props.defaultSortName as ModelSortName<InstanceType<M>>;

	const [filterCount, setFilterCount] = React.useState(0);
	const [sort, setSort] = React.useState<SortState<M, S>>({
		name: props.defaultSortName as string & keyof S,
		value: defaultSortValue,
		way: defaultSortWay,
	});
	const [collapseKey, setCollapseKey] = React.useState<'filters' | 'sorts'>();

	React.useEffect(() => {
		const storedSort: SortState<M, S> = { name: props.defaultSortName as string & keyof S, value: defaultSortValue, way: defaultSortWay };
		const storageFilters: Record<string, any> = {};

		for (const [key, value] of Object.entries(listStore.filters)) {
			switch (key) {
				case 'sortName': {
					const collectionSortKey = Object.entries(collectionSorts)
						.find(collectionSort => {
							return collectionSort[1]?.value === value;
						})?.[0];
					if (collectionSortKey) {
						storedSort['name'] = collectionSortKey;
						storedSort['value'] = value as ModelSortName<InstanceType<M>>;
					}
					break;
				}
				case 'sortWay':
					storedSort['way'] = value as "desc" | "asc";
				break;
				case 'idx':
				case 'page':
				case 'step':
					break;
				default: {
					const collectionFilterKey = Object.entries(collectionFilters)
						.find(collectionFilter => {
							return (collectionFilter[1] as CollectionFilter<M>)?.filter === key;
						})?.[0];
					if (collectionFilterKey) {
						storageFilters[collectionFilterKey] = value;
					}
					break;
				}
			}
		}

		if (!isEqual(sort, storedSort)) {
			setSort(storedSort);
		}
		if (!isEqual(storedFilters, storageFilters)) {
			setStoredFilters(storageFilters);
			setFilterCount(Object.keys(storageFilters).length);
		}
	}, []);

	React.useEffect(() => {
		if (!listStore.isLoading) {
			(async () => {
				const values = filterTools?.getValues() || {};
				const filters = await getFiltersFromCollectionFilters(
					collectionFilters,
					collectionSorts,
					values,
				);

				await listStore?.fetchFilters({
					...listStore.filters,
					...filters,
					search: search,
					sortName: sort.value,
					sortWay: sort.way,
				});
			})();
		}
	}, [sort, search]);

	const ListComponent = (
		<List
			rowKey={m => m.id?.toString() || ''}
			defaultFilters={{
				sortName: props.defaultSortName,
				sortWay: defaultSortWay,
				...props.defaultFilters,
			}}
			storageKey={window.location.pathname + '_list_' + props.modelClass.path}
			fetch={async (page, step, filters) => {
				if (collection instanceof PagedCollection) {
					if (page) {
						collection.setPage(page);
					}

					if (step) {
						collection.setItemsPerPage(step);
					}

					if (filters) {
						collection.setFilters(await getFiltersFromCollectionFilters(collectionFilters, collectionSorts, filters));
					}
				}

				if (props.onFetchCollectionStart) {
					if (await props.onFetchCollectionStart(collection as never) === false) {
						return []; // Si la méthode retourne false, le fetch n'est pas exécuté
					}
				}

				await collection
					.setLocale(props.translations)
					.setIncludeFiles(props.includeFiles)
					.list();

				if (props.onFetchCollectionEnd) {
					await props.onFetchCollectionEnd(collection as never, listStore);
				}

				return collection.map(m => m as any);
			}}
			onClickItem={props.onClickItem || (props.massActions && ((_d, listStore, _e, idx) => {
				listStore.toggleCheckbox(idx);
			}))}
			listStore={listStore}
			checkbox={!!props.massActions}
			{...props}

			pagination={collection instanceof PagedCollection && {
				showSizeChanger: false,
				size: 'default',
				hideOnSinglePage: false,
				pagedCollection: collection,
				pageSize: 20,
				showTotal: total => (
					<Observer>
						{() => (
							<>
								Total : {collection.isLoaded ? total : <OneLineSkeleton width={40} />}
							</>
						)}
					</Observer>
				),
				style: {
					background: theme.colorBgContainer,
					borderTop: `1px solid ${theme.colorBorderSecondary}`,
					bottom: 0,
					padding: '8px 20px',
					position: 'sticky',
				},
				...props.pagination,
			}}
		/>
	);

	return props.massActions || props.collectionFilters || props.collectionSorts ? (
		<View flex className="widesk-list-collection">
			<View px="md" py="sm" gap="xs" row>
				{!!props.massActions?.length && (
					<Tooltip placement="right" title={`Tout ${allChecked ? 'dé' : ''}sélectionner`}>
						<Checkbox
							disabled={!listStore.data.length}
							checked={allChecked}
							indeterminate={(
								listStore.checkboxListIndex.length > 0
								&& listStore.checkboxListIndex.length < listStore.data.length
							)}
							style={{ width: 25 }}
							onChange={() => {
								allChecked ? listStore.clearCheckboxList() : listStore.checkAllCheckbox();
							}} />
					</Tooltip>
				)}
				<InputSearch
					allowClear
					placeholder="Rechercher"
					onChange={_debounce(async (e: any) => {
						const value = e.target.value as string | undefined;
						if (!value && !!search) {
							setSearch(undefined);
						}
						if (value && value.length > 2 && search !== value) {
							setSearch(value);
						}
					}, 600)}
					onSearch={value => setSearch(value || undefined)}
				/>
				{!!props.collectionFilters && (
					<Tooltip title="Filtrer">
						<Badge.Count count={filterCount}>
							<Button
								children={<FilterOutlined />}
								onClick={() => setCollapseKey(collapseKey === 'filters' ? undefined : 'filters')}
								style={{ padding: 8 }}
								type={collapseKey === 'filters' ? 'primary' : 'default'}
							/>
						</Badge.Count>
					</Tooltip>
				)}
				{!!props.collectionSorts && (
					<Tooltip title="Trier">
						<Button
							children={<SortAscendingOutlined />}
							onClick={() => setCollapseKey(collapseKey === 'sorts' ? undefined : 'sorts')}
							style={{ padding: 8 }}
							type={collapseKey === 'sorts' ? 'primary' : 'default'}
						/>
					</Tooltip>
				)}
				<Tooltip title="Rafraîchir">
					<Button
						children={<ReloadOutlined />}
						// Pas d'état `loading` sur ce bouton : le chargement est déjà rendu par la liste
						// (squelettes), et la promesse de `reload()` ne se résout pas si la requête est
						// annulée (voir `ApiCollection.onListError`), ce qui figerait le spinner.
						onClick={() => listStore.reload()}
						style={{ padding: 8 }}
					/>
				</Tooltip>
				{!!props.massActions && !!listStore.checkboxListIndex.length && (
					<Badge.Count count={listStore.checkboxListIndex.length}>
						<ButtonActions
							actions={props.massActions(listStore.checkboxListData as InstanceType<M>[], listStore)}
							forceDropdown
							icon={<BarsOutlined />}
							style={{ padding: 8 }}
							title="Actions sur la sélection"
							type="primary"
						/>
					</Badge.Count>
				)}
			</View>
			<InvisibleCollapse accordion activeKey={collapseKey} items={[
				{
					key: 'filters',
					children: <View className="widesk-list-collection-filters-wrapper" gapV="xs">
						<View centerV flex row spread>
							<View bold>Filtrer :</View>
							<View gapH="xs" row>
								<Tooltip title="Par défault">
									<Button
										onClick={() => {
											filterTools?.resetFields();
											listStore?.fetchFilters({ sortName: sort.value, sortWay: sort.way });
											setCollapseKey(undefined);
											setFilterCount(0);
										}}
										icon={<ClearOutlined />}
									/>
								</Tooltip>
								<Tooltip title="Fermer les filtres">
									<Button onClick={() => setCollapseKey(undefined)} icon={<CloseOutlined />} />
								</Tooltip>
							</View>
						</View>
						<Form
							onDidMount={async formTools => {
								setFilterTools(formTools);
								if (storedFilters) {
									formTools.setFieldsValue(storedFilters);
								}
							}}
							onChange={async formTools => {
								const values = Object.keys(collectionFilters).reduce<Record<string, any>>((acc, cur) => {
									const value = formTools.getFieldValue(cur);
									if (value !== undefined) {
										acc[cur] = value;
									}
									return acc;
								}, {});
								const newFilters = await getFiltersFromCollectionFilters(
									collectionFilters,
									collectionSorts,
									values,
								);

								setFilterCount(Object.keys(newFilters).length);

								await listStore.fetchFilters({
									...newFilters,
									sortName: sort.value,
									sortWay: sort.way,
								});
							}}
							fields={Object.entries(collectionFilters).reduce<FormFields>((acc, [filterName, f]) => {
								const collectionFilter = f as CollectionFilter<M> & { span?: ColSize };

								acc[filterName] = {
									label: collectionFilter.label,
									element: collectionFilter.element,
									span: collectionFilter.span || 24,
								};

								return acc;
							}, {})}
						/>
					</View>,
					showArrow: false,
				},
				{
					key: 'sorts',
					children: <View gapV="xs">
						<View centerV flex row spread>
							<View bold>Trier :</View>
							<View gapH="xs" row>
								<Tooltip title="Par défault">
									<Button
										onClick={() => {
											setSort({
												name: props.defaultSortName as string & keyof S,
												value: defaultSortValue,
												way: defaultSortWay,
											});
											setCollapseKey(undefined);
										}}
										icon={<ClearOutlined />}
									/>
								</Tooltip>
								<Tooltip title="Fermer les tris">
									<Button onClick={() => setCollapseKey(undefined)} icon={<CloseOutlined />} />
								</Tooltip>
							</View>
						</View>
						<Radio.Group onChange={e => {
							const value = (e as RadioChangeEvent).target.value as string;
							const split = value.split('_');
							const sortName = split[0] as string & keyof S;

							setSort({
								name: sortName,
								value: (collectionSorts[sortName] as CollectionSort<M>).value,
								way: split[1] as 'asc' | 'desc',
							});
							setCollapseKey(undefined);
						}} optionType="default" value={`${sort.name}_${sort.way}`} vertical>
							{(Object.entries(collectionSorts)).map(([sortName, s]) => {
								const collectionSort = s as CollectionSort<M>;
								return [
									<Radio.Button value={`${sortName}_asc`}>{collectionSort.labels[0]}</Radio.Button>,
									<Radio.Button value={`${sortName}_desc`}>{collectionSort.labels[1]}</Radio.Button>,
								];
							})}
						</Radio.Group>
					</View>,
					showArrow: false,
				},
			]} />
			{ListComponent}
		</View>
	) : ListComponent;
});

/**
 * Permet de gérer la propriété "filter" lorsqu'il s'agit d'une méthode asynchrone ou d'un string
 */
const getFiltersFromCollectionFilters = async (
	collectionFilters: Record<string, CollectionFilter<any>>,
	collectionSorts: Record<string, { labels: [string, string]; value: string; }>,
	filters: Record<string, unknown>,
) => {
	const result: Record<string, unknown> = {};

	await Promise.all(
		Object.entries(filters).map(
			async ([key, value]) => {
				const collectionFilter = collectionFilters[key];

				if (typeof value === 'undefined' || key === 'sortWay' || (Array.isArray(value) && !value.length)) {
					return;
				}

				if (key === 'sortName' && typeof value === 'string') {
					// `value` est soit une clé de `collectionSorts` (sélecteur de tri visible), soit
					// déjà le nom de champ réel lorsque `collectionSorts` n'est pas fourni (voir
					// `defaultSortName` sur `ListCollectionProps`).
					const collectionSort = collectionSorts[value];
					result[`order[${collectionSort?.value ?? value}]`] = filters['sortWay'] || 'asc';
				} else if (collectionFilter) { // Le filtre est défini dans la liste des filtres de la SplitView
					if (Array.isArray(collectionFilter.filter)) {
						if (!Array.isArray(value)) {
							throw `La "value" pour le filtre "${key}" doit être un tableau`;
						}

						collectionFilter.filter.forEach((fName, idx) => result[fName] = value[idx]);

					} else if (typeof collectionFilter.filter === 'function') { // "filter" est une méthode
						const filterRes = await collectionFilter.filter(value);
						Object.assign(result, filterRes);
					} else { // "filter" est un string
						result[collectionFilter.filter] = value;
					}
				} else {
					result[key] = value;
				}
			},
		),
	);

	return _omit(result, ['idx']);
};

export default ListCollection;
